import "./campaigns.css";

import { Button, Modal, ModalBody, ModalHeader } from "reactstrap";
import { EXPERIMENT_HEX, SiteTile } from "./components/SiteTile";
import {
  EXPERIMENT_STATUS,
  MISSION_MONITORING_STATIC_STATUS,
  OPTIONS_SYSTEM,
  PLOTMAP_STATUS,
  THEME,
  requiresClientAction,
} from "../constants";
import React, { useEffect, useMemo, useState } from "react";
import { contractIsActive, humanize } from "../utils";
import { hasAdminAccess, hasBackofficeAccess } from "../services/roles";
import {
  hasCreateSiteRole,
  hasEditPlotRatingConfigRole,
  hasExperimentsRole,
  hasFeaturesRole,
} from "../users/rolesUtil";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useParams } from "react-router-dom";

import BulkExportDrawer from "./bulkExportDrawer";
import { ButtonAddRemoveUser } from "../components/ButtonAddRemoveUser";
import { CampaignDistribution } from "./components/CampaignDistribution";
import { EmptyState } from "../components/EmptyState";
import { FieldMapSVG } from "../static/svg/FieldMapSVG";
import { FilterBar } from "./components/FilterBar";
import { LevelHeader } from "./components/LevelHeader";
import LoadingImg from "../components/loading";
import { MonitoringRecap } from "./components/MonitoringRecap";
import { PlotRatingConfig } from "./editPlotRatingConfig/plotRatingConfig";
import { PlotSVG } from "../static/svg/PlotSVG";
import { RecapPanel } from "./components/RecapPanel";
import { CfSelect as Select } from "../components/CfSelect";
import { SiteForm } from "./components/SiteForm";
import { SiteTable } from "./components/SiteTable";
import { StatusCounter } from "./components/StatusCounter";
import { UPLOAD_BUCKETS } from "./components/DistributionDonut";
import { UploadCounter } from "./components/UploadCounter";
import { ViewToggle } from "./components/ViewToggle";
import chroma from "chroma-js";
import { fetchCrops } from "../actions/crops";
import { fetchUserContractsTrials } from "../actions/user";
import { formatPeriod } from "./components/format";
import { iso31661 } from "iso-3166";
import { loadMissionsMonitoring } from "../actions/missionMonitoring";
import { requestUploadSummaryBySite } from "../services/backendRequests";

const countryName = (alpha) =>
  iso31661.find((c) => c.alpha3 === alpha)?.name ?? alpha;

const EMPTY_MISSIONS = [];

const SITE_SORTS = {
  name: (a, b) => a.display_name.localeCompare(b.display_name),
  experiment: (a, b) =>
    (b.experiment_status ?? -1) - (a.experiment_status ?? -1),
  plotmap: (a, b) => (b.plotmap_status ?? 0) - (a.plotmap_status ?? 0),
};

// Level 2: a campaign's recap + bento grid of its sites.
const CampaignPage = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { campaignId } = useParams();

  const contracts = useSelector((store) => store.user.contracts) ?? [];
  const loading = useSelector((store) => store.user.contractsLoading);
  const trials = useSelector((store) => store.user.trials) ?? [];
  const user = useSelector((store) => store.user);

  const [siteForm, setSiteForm] = useState({ open: false, site: null });
  const [showPlotRating, setShowPlotRating] = useState(false);
  const [isBulkExportOpen, setIsBulkExportOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [country, setCountry] = useState([]);
  const [crop, setCrop] = useState([]);
  const [experiment, setExperiment] = useState([]);
  const [plotmap, setPlotmap] = useState([]);
  const [upload, setUpload] = useState([]);
  const [results, setResults] = useState([]);
  const [actionRequired, setActionRequired] = useState(false);
  const [missionStatus, setMissionStatus] = useState([]);
  const [sort, setSort] = useState("name");
  const [view, setView] = useState("grid");
  const [siteUploads, setSiteUploads] = useState({});
  const [uploadsLoading, setUploadsLoading] = useState(true);
  const missions =
    useSelector((store) => store.missionMonitoring.byCampaign[campaignId]) ??
    EMPTY_MISSIONS;
  const missionsLoading =
    useSelector((store) => store.missionMonitoring.loading[campaignId]) ??
    false;

  useEffect(() => {
    if (contracts.length === 0) {
      dispatch(fetchUserContractsTrials());
      dispatch(fetchCrops());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  useEffect(() => {
    setUploadsLoading(true);
    requestUploadSummaryBySite(campaignId, user)
      .then(setSiteUploads)
      .catch(() => {})
      .finally(() => setUploadsLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignId]);

  // Load this campaign's monitoring once, cached in redux and reused by the
  // site pages. The role check lives in the thunk.
  useEffect(() => {
    dispatch(loadMissionsMonitoring(campaignId));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignId, contracts]);

  const campaign = contracts.find((contract) => contract.id === campaignId);
  // Only one campaign → the list is a dead end (it redirects straight back), so
  // drop the "Campaigns" link and its back arrow.
  const campaignsTo = contracts.length > 1 ? "/campaigns" : undefined;

  const accent = useMemo(
    () =>
      campaign
        ? chroma(THEME.companyColorHash.hex(campaign.company.name))
            .darken(0.4)
            .hex()
        : undefined,
    [campaign]
  );

  const allSites = campaign?.sites ?? [];

  const countryOptions = useMemo(() => {
    const set = new Set(allSites.map((s) => s.country).filter(Boolean));
    return [...set]
      .map((value) => ({ value, label: countryName(value) }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [allSites]);

  const cropOptions = useMemo(() => {
    const set = new Set(allSites.map((s) => s.crop).filter(Boolean));
    return [...set].sort().map((value) => ({ value, label: value }));
  }, [allSites]);

  // Acquisition statuses carried by each site's missions. A site matches the
  // status filter as soon as one of its missions holds the status, which is what
  // the header KPIs count. Declared before the `sites` memo because that memo
  // reads it during the same render.
  const statusesBySite = useMemo(() => {
    const map = {};
    missions.forEach((mission) => {
      const id = mission.site?.id;
      if (id && mission.status) (map[id] ??= new Set()).add(mission.status);
    });
    return map;
  }, [missions]);

  // Same labels and same order as the monitoring band above: humanized, and
  // sorted along the pipeline rather than alphabetically. Statuses outside the
  // static list keep their arrival order, after the known ones.
  const missionStatusOptions = useMemo(() => {
    const rank = (status) => {
      const index = MISSION_MONITORING_STATIC_STATUS.indexOf(humanize(status));
      return index === -1 ? MISSION_MONITORING_STATIC_STATUS.length : index;
    };
    return [...new Set(missions.map((mission) => mission.status))]
      .filter(Boolean)
      .sort((a, b) => rank(a) - rank(b))
      .map((status) => ({ value: status, label: humanize(status) }));
  }, [missions]);

  const sites = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const filtered = allSites.filter((site) => {
      const matchesQuery =
        !needle ||
        site.display_name?.toLowerCase().includes(needle) ||
        site.crop?.toLowerCase().includes(needle) ||
        countryName(site.country)?.toLowerCase().includes(needle);
      const matchesCountry =
        country.length === 0 || country.includes(site.country);
      const matchesCrop = crop.length === 0 || crop.includes(site.crop);
      const matchesExperiment =
        experiment.length === 0 ||
        experiment.includes(String(site.experiment_status));
      const matchesPlotmap =
        plotmap.length === 0 ||
        plotmap.includes(
          String(site.plotmap_status ?? PLOTMAP_STATUS.NOT_DELIVERED)
        );
      // Upload status lives in siteUploads (async), not on the site. A site
      // matches a bucket when it has ≥1 session in one of the bucket's statuses.
      const matchesUpload =
        upload.length === 0 ||
        upload.some((label) => {
          const bucket = UPLOAD_BUCKETS.find((b) => b.label === label);
          const counts = siteUploads[site.id] ?? {};
          return bucket?.statuses.some((status) => (counts[status] ?? 0) > 0);
        });
      // Results availability = the site has a trial (same signal as the card's
      // green globe pill).
      const hasResult = trials.some((trial) => trial.id === site.id);
      const matchesResults =
        results.length === 0 || results.includes(hasResult ? "yes" : "no");
      const matchesMap = matchesExperiment && matchesPlotmap;
      const matchesAction = !actionRequired || requiresClientAction(site);
      const matchesMissionStatus =
        missionStatus.length === 0 ||
        missionStatus.some((status) => statusesBySite[site.id]?.has(status));
      return (
        matchesQuery &&
        matchesCountry &&
        matchesCrop &&
        matchesMap &&
        matchesUpload &&
        matchesResults &&
        matchesAction &&
        matchesMissionStatus
      );
    });
    return filtered.sort(SITE_SORTS[sort]);
  }, [
    allSites,
    query,
    country,
    crop,
    experiment,
    plotmap,
    upload,
    results,
    actionRequired,
    missionStatus,
    statusesBySite,
    trials,
    siteUploads,
    sort,
  ]);

  // Counted on the sites actually on screen, not on the whole campaign: the
  // banner used to promise "3 sites require your attention" while a filter hid
  // all three, so clicking it landed on an empty list.
  const actionCount = sites.filter(requiresClientAction).length;

  // Per-site monitoring signals: active flags + unread comments summed across
  // the site's missions, so each card can flag what needs attention.
  const siteMonitoring = useMemo(() => {
    const map = {};
    missions.forEach((mission) => {
      const id = mission.site?.id;
      if (!id) return;
      const entry = (map[id] ??= { flags: 0, comments: 0 });
      entry.flags += mission.count_active_flags ?? 0;
      entry.comments += mission.count_unread_comments ?? 0;
    });
    return map;
  }, [missions]);

  // Campaign-level upload totals = sum of every site's status counts.
  const uploadTotal = useMemo(() => {
    const aggregate = { total: 0 };
    Object.values(siteUploads).forEach((counts) => {
      Object.entries(counts).forEach(([key, value]) => {
        aggregate[key] = (aggregate[key] ?? 0) + value;
      });
    });
    return aggregate;
  }, [siteUploads]);

  if (!campaign)
    return (
      <div className="page-content container-fluid cf-page">
        <LevelHeader
          path={[
            { label: "Campaigns", to: campaignsTo },
            { label: "Campaign" },
          ]}
        />
        {loading ? (
          <LoadingImg visible />
        ) : (
          <EmptyState
            icon="fa-regular fa-folder-open"
            title="Campaign not found"
            text="It may have been removed or you no longer have access."
          />
        )}
      </div>
    );

  const actionsVisible =
    hasBackofficeAccess(user.self) || hasAdminAccess(user.self);
  const canEdit = hasCreateSiteRole(campaign.roles);
  // Bulk export is hidden when no site of the campaign has data to export, or
  // when the user may not read its features (the backend would refuse).
  const hasExportableData =
    hasFeaturesRole(campaign.roles) &&
    (campaign.sites || []).some((site) =>
      trials.some((trial) => trial.id === site.id && trial.trial_dates?.length)
    );
  const showExperiment =
    campaign.has_experiment && hasExperimentsRole(campaign.roles);
  const sortOptions = [
    { value: "name", label: "Sort: Name" },
    { value: "plotmap", label: "Sort: Plot map status" },
    ...(showExperiment
      ? [{ value: "experiment", label: "Sort: Field map" }]
      : []),
  ];

  const onSaved = () => {
    dispatch(fetchUserContractsTrials());
    setSiteForm({ open: false, site: null });
  };

  const viewToggle = (
    <ViewToggle
      value={view}
      onChange={setView}
      ariaLabel="Sites layout"
      options={[
        { value: "grid", label: "Grid", icon: "fa-table-cells-large" },
        { value: "list", label: "List", icon: "fa-list" },
      ]}
    />
  );

  // Distribution legend → top filter. Each donut dimension maps to one filter;
  // clicking a segment selects it, clicking the sole-selected one clears it.
  const applyFilter = (dimension, value) => {
    const setters = {
      plotmap: setPlotmap,
      crop: setCrop,
      country: setCountry,
      experiment: setExperiment,
      uploads: setUpload,
      results: setResults,
    };
    const setter = setters[dimension];
    if (setter)
      setter((prev) => (prev.length === 1 && prev[0] === value ? [] : [value]));
  };

  // Every filter, including the ones the FilterBar doesn't own (the search box
  // and the action-required toggle). `sort` and `view` are not filters: they
  // change how the same set is presented, so they stay put.
  const clearAllFilters = () => {
    setQuery("");
    setCountry([]);
    setCrop([]);
    setExperiment([]);
    setPlotmap([]);
    setUpload([]);
    setResults([]);
    setActionRequired(false);
    setMissionStatus([]);
  };

  const headerActions = (
    <>
      {/* Labelled, not a bare pencil: the site tiles right below carry an
          identical pencil for editing a site, so two neighbouring pencils did
          two different things on one screen (R1-25a). The tooltip goes with the
          icon, the label says it out loud. */}
      {hasEditPlotRatingConfigRole(campaign.roles) && (
        <Button
          color="primary"
          outline
          className="text-nowrap"
          onClick={() => setShowPlotRating(true)}
        >
          <i className="fa-solid fa-pencil me-1" /> Rating configuration
        </Button>
      )}
      {hasExportableData && (
        <Button
          color="success"
          className="text-nowrap"
          onClick={() => setIsBulkExportOpen(true)}
        >
          <i className="fa-solid fa-download me-1" /> Download results
        </Button>
      )}
      {actionsVisible && (
        <>
          <ButtonAddRemoveUser
            contract={campaign}
            company={campaign.company}
            hasContractAccess
            className="p-0"
          />
          <Button
            color="link"
            className="p-1"
            data-tooltip-id="tooltip"
            data-tooltip-content="Open in administration"
            onClick={() =>
              history.push(
                `/administration/company/${campaign.company.id}/contract/${campaign.id}`
              )
            }
          >
            <i className="fa-solid fa-lg fa-gears" />
          </Button>
        </>
      )}
    </>
  );

  const experimentOptions = Object.entries(EXPERIMENT_STATUS.properties).map(
    ([value, { label }]) => ({ value, label })
  );
  const plotmapOptions = Object.entries(PLOTMAP_STATUS.properties).map(
    ([value, { label }]) => ({ value, label })
  );

  // Count sites per status of a given field → coloured-dot segments for the
  // recap, reusing the status colours from the site tiles.
  const statusSegments = (field, fallback, properties, colorOf) =>
    Object.entries(properties).map(([key, meta]) => ({
      label: meta.label,
      value: allSites.filter((s) => String(s[field] ?? fallback) === key)
        .length,
      color: colorOf(meta),
      filterValue: key,
    }));

  const plotmapSegments = statusSegments(
    "plotmap_status",
    PLOTMAP_STATUS.NOT_DELIVERED,
    PLOTMAP_STATUS.properties,
    (meta) => meta.color
  );
  const experimentSegments = statusSegments(
    "experiment_status",
    EXPERIMENT_STATUS.NOT_REQUIRED,
    EXPERIMENT_STATUS.properties,
    (meta) => EXPERIMENT_HEX[meta.color] ?? "#9aa0a6"
  );

  // Results availability across the sites (same green-globe signal as the cards).
  const sitesWithResults = allSites.filter((site) =>
    trials.some((trial) => trial.id === site.id)
  ).length;
  const resultsSegments = [
    {
      label: "With results",
      value: sitesWithResults,
      color: "#81b23d",
      filterValue: "yes",
    },
    {
      label: "No results",
      value: allSites.length - sitesWithResults,
      color: "#c0c0c0",
      filterValue: "no",
    },
  ];

  // Recap rows for the left panel. Plain facts pair up in the 2-column grid
  // first; the full-width status counters are grouped together at the bottom so
  // the pairing never gets orphaned by an interleaved wide row. Field map
  // (Enabled) & upload template are internal details (backoffice/admin only).
  const recapItems = [
    { label: "Sites", value: campaign.total_sites ?? 0 },
    {
      label: "Period",
      value: formatPeriod(campaign.start_date, campaign.end_date),
    },
    campaign.system && {
      label: "System",
      value:
        OPTIONS_SYSTEM.find((o) => o.value === campaign.system)?.label ??
        campaign.system,
    },
    actionsVisible &&
      campaign.has_experiment && { label: "Field map", value: "Enabled" },
    actionsVisible &&
      campaign.upload_template && {
        label: "Upload template",
        value: campaign.upload_template,
      },
    {
      wide: true,
      value: (
        <div className="cf-recap2__counters">
          {uploadTotal.total > 0 && (
            <div className="cf-recap2__counter">
              <span className="cf-recap2__sublabel">Upload sessions</span>
              <UploadCounter
                counts={uploadTotal}
                onSelect={(label) => applyFilter("uploads", label)}
              />
            </div>
          )}
          <div className="cf-recap2__counter">
            <span className="cf-recap2__sublabel">Results</span>
            <StatusCounter
              icon={
                <i className="fa-solid fa-earth-europe" aria-hidden="true" />
              }
              segments={resultsSegments}
              onSelect={(value) => applyFilter("results", value)}
            />
          </div>
        </div>
      ),
    },
    {
      wide: true,
      value: (
        <div className="cf-recap2__counters">
          {showExperiment && (
            <div className="cf-recap2__counter">
              <span className="cf-recap2__sublabel">Field maps</span>
              <StatusCounter
                icon={
                  <FieldMapSVG
                    width="13px"
                    height="13px"
                    fill="#1e3cd2"
                    aria-hidden="true"
                  />
                }
                segments={experimentSegments}
                onSelect={(value) => applyFilter("experiment", value)}
              />
            </div>
          )}
          <div className="cf-recap2__counter">
            <span className="cf-recap2__sublabel">Plot maps</span>
            <StatusCounter
              icon={
                <PlotSVG
                  width="13px"
                  height="13px"
                  fill="currentColor"
                  aria-hidden="true"
                />
              }
              segments={plotmapSegments}
              onSelect={(value) => applyFilter("plotmap", value)}
            />
          </div>
        </div>
      ),
    },
  ].filter(Boolean);

  return (
    <div className="page-content container-fluid cf-page">
      <Modal
        size="md"
        isOpen={siteForm.open}
        toggle={() => setSiteForm({ open: false, site: null })}
      >
        <ModalHeader toggle={() => setSiteForm({ open: false, site: null })}>
          Edit site
        </ModalHeader>
        <ModalBody>
          {siteForm.open && (
            <SiteForm
              contract={campaign}
              site={siteForm.site}
              user={user}
              onSaved={onSaved}
              onCancel={() => setSiteForm({ open: false, site: null })}
            />
          )}
        </ModalBody>
      </Modal>

      <Modal
        size="md"
        isOpen={showPlotRating}
        toggle={() => setShowPlotRating(false)}
      >
        <ModalHeader toggle={() => setShowPlotRating(false)}>
          <h4 className="mb-0">
            {campaign.name}
            {": "}
            <small>Plot rating configuration</small>
          </h4>
        </ModalHeader>
        <ModalBody>
          <PlotRatingConfig contract={campaign} user={user} />
        </ModalBody>
      </Modal>

      <LevelHeader
        path={[
          { label: "Campaigns", to: campaignsTo },
          { label: campaign.name },
        ]}
        metrics={[{ value: campaign.total_sites ?? 0, label: "Sites" }]}
        actions={headerActions}
      />

      {/* Mounted only while open so each opening starts with nothing checked.
          Scoped to this campaign only. */}
      {isBulkExportOpen && (
        <BulkExportDrawer
          isOpen
          onClose={() => setIsBulkExportOpen(false)}
          contracts={[campaign]}
          trials={trials}
          user={user}
        />
      )}

      <MonitoringRecap
        missions={missions}
        loading={missionsLoading}
        // The band drills on the status only when it is the sole one selected:
        // a multi-status filter has no single substatus breakdown to show.
        selectedStatus={
          missionStatus.length === 1 ? missionStatus[0] : undefined
        }
        onSelectStatus={(status) =>
          setMissionStatus((prev) =>
            // undefined = the band's "Clear statuses" button.
            status == null || (prev.length === 1 && prev[0] === status)
              ? []
              : [status]
          )
        }
        detailTo={
          // The monitoring dashboard lists the same contracts: a deep-link to
          // one it drops lands on an unrelated campaign.
          contractIsActive(campaign)
            ? `/monitoring?contract=${campaignId}`
            : undefined
        }
      />

      <div className="cf-split">
        <aside className="cf-split__aside">
          <RecapPanel
            header={campaign.company.name}
            icon="fa-solid fa-building"
            accentColor={accent}
            items={recapItems}
          />
          {allSites.length > 0 && (
            <CampaignDistribution
              sites={allSites}
              showExperiment={showExperiment}
              uploadTotal={uploadTotal}
              uploadsLoading={uploadsLoading}
              accentColor={accent}
              onFilter={applyFilter}
            />
          )}
        </aside>

        <div className="cf-split__main">
          {allSites.length > 0 && (
            <FilterBar
              query={query}
              onQuery={setQuery}
              placeholder="Search a site, crop or country…"
              onClearAll={clearAllFilters}
              extraActive={actionRequired}
              compact
              selects={[
                {
                  label: "Country",
                  value: country,
                  onChange: setCountry,
                  allLabel: "All countries",
                  options: countryOptions,
                },
                {
                  label: "Crop",
                  value: crop,
                  onChange: setCrop,
                  allLabel: "All crops",
                  options: cropOptions,
                },
                ...(showExperiment
                  ? [
                      {
                        label: "Field map",
                        value: experiment,
                        onChange: setExperiment,
                        allLabel: "All field maps",
                        options: experimentOptions,
                      },
                    ]
                  : []),
                {
                  label: "Plot map",
                  value: plotmap,
                  onChange: setPlotmap,
                  allLabel: "All plot maps",
                  options: plotmapOptions,
                },
                ...(uploadTotal.total > 0
                  ? [
                      {
                        label: "Upload sessions",
                        value: upload,
                        onChange: setUpload,
                        allLabel: "All uploads",
                        options: UPLOAD_BUCKETS.map((b) => ({
                          value: b.label,
                          label: b.label,
                        })),
                      },
                    ]
                  : []),
                {
                  label: "Results",
                  value: results,
                  onChange: setResults,
                  allLabel: "All results",
                  options: [
                    { value: "yes", label: "With results" },
                    { value: "no", label: "Without results" },
                  ],
                },
                // Always passed, even with no option: FilterBar hides an empty
                // select on its own, but only a select it was handed counts as
                // active. Gating it here hid the Clear all button too, leaving
                // the list filtered with no control on screen.
                {
                  label: "Acquisition",
                  value: missionStatus,
                  onChange: setMissionStatus,
                  allLabel: "All acquisitions",
                  options: missionStatusOptions,
                },
              ]}
              extra={
                <Select
                  className="cf-rs"
                  classNamePrefix="cf-rs"
                  isSearchable={false}
                  options={sortOptions}
                  value={sortOptions.find((option) => option.value === sort)}
                  onChange={(option) => setSort(option.value)}
                  aria-label="Sort sites"
                />
              }
              trailing={
                <>
                  {(actionCount > 0 || actionRequired) && (
                    <label className="btn-action-required">
                      <input
                        type="checkbox"
                        checked={actionRequired}
                        onChange={() => setActionRequired((v) => !v)}
                      />
                      <i className="fa-solid fa-triangle-exclamation" />
                      <span>
                        {actionCount}{" "}
                        {actionCount === 1 ? "site requires" : "sites require"}{" "}
                        your attention
                      </span>
                    </label>
                  )}
                  {viewToggle}
                </>
              }
            />
          )}

          {sites.length === 0 ? (
            <EmptyState
              icon="fa-regular fa-map"
              title={
                allSites.length === 0
                  ? "No site yet"
                  : "No site matches your filters"
              }
              text={
                allSites.length === 0
                  ? "Sites of this campaign will appear here."
                  : "Try adjusting the search or filters."
              }
            />
          ) : view === "list" ? (
            <SiteTable
              sites={sites}
              showExperiment={showExperiment}
              trials={trials}
              monitoring={siteMonitoring}
              canEdit={canEdit}
              onOpen={(s) =>
                history.push(`/campaigns/${campaign.id}/sites/${s.id}`)
              }
              onEdit={(s) => setSiteForm({ open: true, site: s })}
              onViewResults={(s) => history.push(`/map/${s.id}`)}
            />
          ) : (
            <div className="cf-bento">
              {sites.map((site) => (
                <SiteTile
                  key={site.id}
                  site={site}
                  showExperiment={showExperiment}
                  showTechnicalName={actionsVisible}
                  hasResults={trials.some((trial) => trial.id === site.id)}
                  canEdit={canEdit}
                  uploads={siteUploads[site.id]}
                  monitoring={siteMonitoring[site.id]}
                  onOpen={(s) =>
                    history.push(`/campaigns/${campaign.id}/sites/${s.id}`)
                  }
                  onEdit={(s) => setSiteForm({ open: true, site: s })}
                  onViewResults={(s) => history.push(`/map/${s.id}`)}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CampaignPage;
